import type { PageSection, Site, SitePage } from '../types/site.types';

export const CONTACT_SECTION_TYPE = 'ContactSection';

export const CONTACT_EMAIL_MISSING_ERROR_KEY = 'generic.errors.contact-email-missing';
export const CONTACT_EMAIL_INVALID_ERROR_KEY = 'generic.errors.contact-email-invalid';

export type ValidatablePage = Omit<Partial<SitePage>, 'sections'> & {
  sections?: Array<Partial<PageSection>>;
};

export type ValidatableSite = Omit<Partial<Site>, 'pages'> & {
  contactEmail?: string;
  pages?: ValidatablePage[];
};

function isVisibleContactSection(section: Partial<PageSection>): boolean {
  return section.type === CONTACT_SECTION_TYPE && !section.hidden;
}

export function hasContactSection(site: ValidatableSite | undefined): boolean {
  if (!site?.pages) return false;
  return site.pages.some((page) => (page.sections || []).some(isVisibleContactSection));
}

// Basic format check only (something@something.tld), same rule as login.utils isValidEmail
const SIMPLE_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when a non-blank contact email is set (regardless of its format). */
export function hasContactEmail(site: ValidatableSite | undefined): boolean {
  const email = site?.contactEmail;
  return typeof email === 'string' && email.trim().length > 0;
}

/** True when a contact email is set and looks like an email. */
export function hasValidContactEmail(site: ValidatableSite | undefined): boolean {
  const email = site?.contactEmail;
  return typeof email === 'string' && SIMPLE_EMAIL_REGEX.test(email.trim());
}

/** Contact section present but no contact email set at all. */
export function isContactEmailMissing(site: ValidatableSite | undefined): boolean {
  return hasContactSection(site) && !hasContactEmail(site);
}

/** Contact section present, contact email set but malformed. */
export function isContactEmailInvalid(site: ValidatableSite | undefined): boolean {
  return hasContactSection(site) && hasContactEmail(site) && !hasValidContactEmail(site);
}

/**
 * Returns the list of i18n error keys preventing the site from being saved.
 * Empty array means the site is consistent and can be saved.
 */
export function getSiteSaveErrors(site: ValidatableSite | undefined): string[] {
  const errors: string[] = [];
  if (isContactEmailMissing(site)) {
    errors.push(CONTACT_EMAIL_MISSING_ERROR_KEY);
  } else if (isContactEmailInvalid(site)) {
    errors.push(CONTACT_EMAIL_INVALID_ERROR_KEY);
  }
  return errors;
}
